/**
 * Маппинг analytics-целей «Подбора» (ЛК) → серверный mode групповой проценки.
 *
 * Eplex 1С серверно поддерживает 4 режима: `manual`, `tender`, `pricekeeper`,
 * `retail`. Никакого «competitor / monitoring / pricing» режима в 1С нет —
 * это новинка ЛК. Маппим следующим образом:
 *
 * - `competitors` → `manual` (разовая проценка по выбранным плагинам-конкурентам);
 *   роль «конкурент» — FE-тег, на бэке плагин обычный.
 * - `monitoring`  → `pricekeeper` (циклический с триггерами `top_price`,
 *   `price_diff`, `min_offer_count`, `not_found`).
 * - `pricing`     → `tender` (отчёты с агрегированной выдачей; client_period
 *   позволяет повторять, report_delivery — рассылать).
 * - `forecast`    → mock (нет серверной поддержки).
 *
 * Контракт сверен с расшифрованным BSL Eplex 1С (eplex_1c_decoded.md §13-15),
 * пишем те же поля и в том же порядке, что и 1С — это минимизирует расхождения
 * при работе с уже существующими GS, созданными из 1С.
 */

import type { SelectionAnalyticsConfig, SelectionGoal, SelectionStrategy } from "../../../models/eplex/Selection";
import type {
  GsAnalyze,
  GsMode,
  GsNotify,
  GsPricekeeperSettings,
  GsRetailSettings,
  GsSettings,
  GsTenderSettings,
  GsTrigger,
} from "../../../api/groupSearchApi";
import { GS_TERM_NO_LIMIT } from "./gsTermLimit";
import { GS_INITIAL_DELIVERY_TERM_KEY } from "./gsDeliveryTerm";
import { resolveDeliveryTerm } from "./smartFiltersStorage";

/** Goals, маршрутизируемые на живой group_search API. */
export const GS_LIVE_GOALS: SelectionGoal[] = ["other", "competitors", "monitoring", "pricing"];

export function isGsLiveGoal(goal: SelectionGoal): boolean {
  return GS_LIVE_GOALS.includes(goal);
}

export function mapGoalToMode(goal: SelectionGoal): GsMode | null {
  // «Другое» — классическая упрощённая ГП: тот же manual-режим, что и сверка,
  // но в UI показываем простой результат (потребность → плагин → таблица), без pivot.
  if (goal === "other") return "manual";
  if (goal === "competitors") return "manual";
  if (goal === "monitoring") return "pricekeeper";
  if (goal === "pricing") return "tender";
  return null;
}

/**
 * Обратный маппинг — восстановить analytics-цель из серверного `settings.mode`.
 * Нужен при загрузке списка GS с бэка: localStorage `goalByUuid` есть только
 * для GS, созданных в текущей сессии. Для остальных guess по mode:
 *  - `manual` → competitors (одноразовая сверка);
 *  - `pricekeeper` → monitoring (мониторинг с триггерами);
 *  - `tender` → pricing (периодический ценовой отчёт);
 *  - `retail` → competitors (нечастый случай, дефолтим в competitors).
 */
export function modeToAnalyticsGoal(mode: GsMode): SelectionGoal {
  if (mode === "pricekeeper") return "monitoring";
  if (mode === "tender") return "pricing";
  // manual/retail → «Другое»: единственный активный manual-сценарий ЛК
  // (сверка с конкурентами пока отключена).
  return "other";
}

/**
 * Дни между прогонами для pricekeeper/tender/retail режимов на основании
 * выбора пользователя в Wizard (once/daily/weekly/monthly). `once` = 0
 * (one-off) — серверная семантика: «не повторять».
 */
export function scheduleToClientPeriod(schedule: SelectionAnalyticsConfig["schedule"]): number {
  if (schedule === "daily") return 1;
  if (schedule === "weekly") return 7;
  if (schedule === "monthly") return 30;
  return 0;
}

/**
 * Триггеры ЛК → GS-формат. 1С шлёт каждый триггер как `{type, params:[{name,value}]}`.
 * У триггера `not_found` нет параметров.
 */
function buildTriggers(triggers: SelectionAnalyticsConfig["triggers"]): GsTrigger[] | undefined {
  if (!triggers || triggers.length === 0) return undefined;
  const result: GsTrigger[] = [];
  for (const trigger of triggers) {
    if (trigger.type === "not_found") {
      result.push({ type: "not_found", params: [] });
      continue;
    }
    const paramName = paramNameFor(trigger.type);
    if (!paramName) continue;
    result.push({
      type: trigger.type,
      params: [{ name: paramName, value: trigger.param ?? defaultParamValueFor(trigger.type) }],
    });
  }
  return result;
}

function paramNameFor(type: GsTrigger["type"]): string | null {
  if (type === "top_price") return "lowest_allowed_position";
  if (type === "price_diff") return "max_diff_percent";
  if (type === "min_offer_count") return "count";
  return null;
}

function defaultParamValueFor(type: GsTrigger["type"]): number {
  if (type === "top_price") return 3;
  if (type === "price_diff") return 30;
  if (type === "min_offer_count") return 2;
  return 0;
}

/**
 * Базовый блок `analyze`. Поле `max_time` 1С опускает, если значение ≥ 21 —
 * сервер интерпретирует отсутствие как «без ограничения».
 */
function buildAnalyze(strategy: SelectionStrategy): GsAnalyze {
  const analyze: GsAnalyze = {
    category: [2, 3],
    order_by: "cost",
  };
  if (strategy.maxTime > 0 && strategy.maxTime < GS_TERM_NO_LIMIT) {
    analyze.max_time = strategy.maxTime;
  }
  if (strategy.priceDiffPercent > 0) {
    analyze.cost_excess_percent = strategy.priceDiffPercent;
  }
  return analyze;
}

function buildNotify(analytics: SelectionAnalyticsConfig): GsNotify {
  return {
    email: analytics.notifyEmail || undefined,
    on_finish: analytics.notifyOnFinish,
    on_stop: analytics.notifyOnFinish,
  };
}

/**
 * Собрать полный `settings` для GS create/update.
 *
 * Контракт mode-specific блоков:
 *  - manual    → ничего сверх базовых полей (1С тоже ничего не добавляет);
 *  - pricekeeper → `pricekeeper.{client_period, max_searches_per_day, max_time?, triggers, plugin_rules:{},exception_rules:{}}`
 *  - tender    → `tender.{client_period, period_distribute, geolocation, max_time?, plugin_filters:{}, report_type, report_delivery}`
 *                плюс дублирование `report_type`/`report_delivery` на верхний
 *                уровень `settings` (так делает 1С — оставлено для совместимости).
 */
export function buildGsSettings(input: {
  goal: SelectionGoal;
  title: string;
  pluginIds: number[];
  strategy: SelectionStrategy;
  analytics: SelectionAnalyticsConfig;
}): GsSettings {
  const mode = mapGoalToMode(input.goal);
  if (mode === null) {
    throw new Error(`Goal "${input.goal}" не поддерживается group_search API`);
  }

  const settings: GsSettings = {
    name: input.title,
    mode,
    plugins: input.pluginIds.filter((id) => id > 0),
    notify: buildNotify(input.analytics),
    analyze: buildAnalyze(input.strategy),
  };

  if (input.goal === "other") {
    // В ручном подборе срок — только начальный фильтр ЛК. Не ограничиваем поиск
    // на сервере, чтобы ползунок мог показать более долгие предложения.
    delete settings.analyze.max_time;
    settings.add_info = { [GS_INITIAL_DELIVERY_TERM_KEY]: resolveDeliveryTerm(input.strategy.maxTime) };
  }

  if (mode === "pricekeeper") {
    const pk: GsPricekeeperSettings = {
      client_period: scheduleToClientPeriod(input.analytics.schedule) || 30,
      max_searches_per_day: 1000,
      use_price_from_site: true,
      triggers: buildTriggers(input.analytics.triggers),
      plugin_rules: {},
      exception_rules: {},
    };
    if (input.strategy.maxTime > 0 && input.strategy.maxTime < GS_TERM_NO_LIMIT) {
      pk.max_time = input.strategy.maxTime;
    }
    settings.pricekeeper = pk;
  } else if (mode === "tender") {
    const tender: GsTenderSettings = {
      client_period: scheduleToClientPeriod(input.analytics.schedule),
      period_distribute: false,
      geolocation: "Москва",
      plugin_filters: {},
      report_type: 1,
      report_delivery: input.analytics.notifyEmail
        ? [
            {
              type: "email",
              settings: { delivery_type: "on_finish", template: 1 },
            },
          ]
        : undefined,
    };
    if (input.strategy.maxTime > 0 && input.strategy.maxTime < GS_TERM_NO_LIMIT) {
      tender.max_time = input.strategy.maxTime;
    }
    settings.tender = tender;
    settings.report_type = tender.report_type;
    settings.report_delivery = tender.report_delivery;
  } else if (mode === "retail") {
    const retail: GsRetailSettings = {
      client_period: scheduleToClientPeriod(input.analytics.schedule),
      period_distribute: false,
      geolocation: "Москва",
      plugin_filters: {},
    };
    settings.retail = retail;
  }

  return settings;
}

/**
 * Маппинг серверного status GS → SelectionState ЛК. Значения статусов сняты
 * посекундным замером живой проценки на стенде (CAB-738):
 *  0 — черновик (создан, не запущен)
 *  1 — в работе: есть позиция в обработке либо очередь ждёт следующей
 *  2 — переходный: `/stop` принят, очередь ещё гаснет
 *  3 — остановлен пользователем
 *  4 — завершён (finished = total)
 *  6 — **пауза между позициями**, а не завершение: `in_progress` = 0, но
 *      `finished < total`; между запусками бэк держит min_interval ~7 с, и всё
 *      это время проценка отдаёт 6, чередуя его с 1 до самого конца.
 *  7 — pricekeeper / tender: цикл завершён, ждёт следующего по `client_period`.
 *
 * Отсюда правило: одного статуса мало — финальность определяем по очереди.
 * Раньше 6 считался завершением, и в середине проценки карточка показывала
 * «Завершено», поллинг снимался (прогресс замирал = «зависла»), а «Остановить»
 * гасло — при том, что на бэке проценка продолжала работать и тратить лимит.
 */
export function mapGsStatusToSelectionState(
  status: number,
  info?: { total?: number; finished?: number; in_progress?: number },
): "draft" | "pricing" | "completed" | "cancelled" {
  if (status === 0) return "draft";
  if (status === 3) return "cancelled";
  if (status === 4) return "completed";

  const isWorkingStatus = status === 1 || status === 2 || status === 6;
  if (!info) return isWorkingStatus ? "pricing" : "completed";
  if ((info.in_progress ?? 0) > 0) return "pricing";

  const total = info.total ?? 0;
  const finished = info.finished ?? 0;
  const hasUnprocessed = total > 0 && finished < total;
  return hasUnprocessed && isWorkingStatus ? "pricing" : "completed";
}
